<!--
  Edit set: name, set date (defaults to the earliest capture; a progression orders its sets by it),
  which progression it belongs to (at most one, which can be renamed here), the order of its items
  (drag, or ‹ › on touch); each thumbnail opens its picture large, and its ✎ edits that item's details;
  add or take out items (taking one out leaves it in the library), assign the whole set to another
  patient (refused when it would split an item away from another set), and remove the set
  (its items stay). The layout decides where it sits; it reports how it ended (saved, deleted, or
  moved to another patient) so the layout can show what comes next.
-->
<template>
  <section class="md-seteditor" :class="`is-${layout}`" aria-label="Edit set">
    <header class="md-seteditor-head">
      <button v-if="backLabel" class="md-back" @click="$emit('back')">‹ {{ backLabel }}</button>
      <h2 class="md-grow">Edit set</h2>
    </header>
    <div v-if="!set" class="md-seteditor-body md-muted">Loading…</div>
    <div v-else class="md-seteditor-body">
      <div class="md-seteditor-two">
        <div class="md-field">
          <label for="md-se-name">Set name</label>
          <input id="md-se-name" v-model="name" class="md-input" maxlength="150" />
        </div>
        <div class="md-field">
          <label for="md-se-date">Set date</label>
          <input id="md-se-date" v-model="setDate" type="date" class="md-input" />
        </div>
      </div>
      <div class="md-field">
        <label for="md-se-prog">Progression</label>
        <div class="md-row">
          <select id="md-se-prog" v-model="progressionId" class="md-select md-grow" style="width:auto">
            <option :value="null">None (unsorted)</option>
            <option v-for="p in progressions" :key="p.id" :value="p.id">{{ p.name }} · {{ p.count }} set{{ p.count === 1 ? '' : 's' }}</option>
          </select>
          <button v-if="progressionId" class="md-btn ghost sm" @click="renameProgression">Rename</button>
          <button class="md-btn ghost sm" @click="newProgression">+ New progression</button>
        </div>
        <span class="md-small md-muted">A set belongs to one progression at most; the progression shows its sets in set-date order.</span>
      </div>

      <div class="md-row" style="margin-bottom:8px">
        <strong>{{ order.length }} item{{ order.length === 1 ? '' : 's' }} in this set</strong>
        <span class="md-grow" />
        <span class="md-small md-muted">Drag to reorder</span>
      </div>
      <div v-if="preview" class="md-seteditor-preview">
        <div class="md-seteditor-preview-stage"><MediaFull :item="preview" /></div>
        <button class="md-btn sm md-seteditor-preview-back" @click="preview = null">Back to thumbnails</button>
        <p class="md-small md-muted">{{ fmt(preview.captured_at) }} · {{ preview.note || 'No note.' }}</p>
      </div>
      <div v-show="!preview" class="md-seteditor-items">
        <div v-for="(it, i) in order" :key="it.id" class="md-seteditor-item" :class="{ dragging: dragFrom === i, over: dragOver === i }"
          draggable="true" @dragstart="dragFrom = i" @dragover.prevent="dragOver = i" @dragleave="dragOver = null" @drop.prevent="dropAt(i)" @dragend="dragFrom = dragOver = null">
          <span class="md-seteditor-pos">{{ i + 1 }}</span>
          <button class="md-seteditor-edit" :aria-label="`Edit details of item ${i + 1}`" title="Edit details" @click="startItemEdit(it)">✎</button>
          <!-- Not a <button>: a drag that starts on a button never reaches the draggable tile. -->
          <div class="md-seteditor-open" role="button" tabindex="0" :aria-label="`Open item ${i + 1}`" @click="preview = it" @keydown.enter="preview = it"><MediaThumb :item="it" /></div>
          <div class="md-small md-seteditor-meta">{{ fmt(it.captured_at) }}</div>
          <div class="md-small md-muted md-seteditor-note">{{ it.note || 'No note' }}</div>
          <div class="md-row md-seteditor-actions">
            <button class="md-iconbtn" :disabled="i === 0" aria-label="Move earlier" @click="move(i, -1)">‹</button>
            <button class="md-iconbtn" :disabled="i === order.length - 1" aria-label="Move later" @click="move(i, 1)">›</button>
            <button class="md-link md-small" @click="takeOut(it)">Take out</button>
          </div>
        </div>
      </div>
      <div v-if="itemDraft" class="md-card md-seteditor-itemedit">
        <div class="md-row"><strong>Details of item {{ order.findIndex((x) => x.id === itemDraft.id) + 1 }}</strong></div>
        <MediaTagForm v-model="itemDraft.fields" />
        <div class="md-row" style="justify-content:flex-end">
          <button class="md-btn ghost" @click="itemDraft = null">Cancel</button>
          <button class="md-btn" @click="saveItemEdit">Save details</button>
        </div>
      </div>
      <p v-if="takenOut.length" class="md-small md-muted">{{ takenOut.length }} item{{ takenOut.length === 1 ? '' : 's' }} will leave this set on save (they stay in the patient’s library).</p>

      <div v-if="adding" class="md-card md-seteditor-add">
        <div class="md-row"><strong>Add from {{ set.patient_name }}’s media</strong><span class="md-grow" /><button class="md-link" @click="adding = false">Done</button></div>
        <div v-if="!candidates.length" class="md-muted md-small">Nothing else in this patient’s library.</div>
        <div class="md-grid">
          <button v-for="it in candidates" :key="it.id" class="md-tile" :class="{ selected: toAdd.includes(it.id) }" @click="toggleAdd(it.id)">
            <MediaThumb :item="it" />
            <span class="meta"><span class="t">{{ fmt(it.captured_at) }}</span><span class="md-muted">{{ it.note || '—' }}</span></span>
          </button>
        </div>
      </div>

      <div v-if="splitError" class="md-banner md-seteditor-split">
        {{ splitError }}
      </div>
    </div>
    <footer v-if="set" class="md-seteditor-foot">
      <button class="md-btn ghost" @click="openAdd">+ Add media</button>
      <button class="md-btn ghost" @click="reassign">Assign to different patient</button>
      <button class="md-btn danger" @click="removeSet">Delete set</button>
      <span class="md-grow" />
      <button class="md-btn" :disabled="!name.trim()" @click="save">Save</button>
    </footer>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import Swal from 'sweetalert2';
import { useMediaStore } from '@/stores/mediaStore';
import { getMediaSet, listPatientMedia, patientName, reassignMedia } from '@/api/mediaApi';
import MediaThumb from './MediaThumb.vue';
import MediaFull from './MediaFull.vue';
import MediaTagForm from './MediaTagForm.vue';

const props = defineProps({
  setId: { type: Number, required: true },
  layout: { type: String, default: 'page' },
  backLabel: { type: String, default: '' },
});
const emit = defineEmits(['back', 'saved', 'deleted', 'moved']);
const store = useMediaStore();

const set = ref(null);
const name = ref('');
const setDate = ref('');
const progressionId = ref(null);
const progressions = ref([]);
const order = ref([]);
const takenOut = ref([]);
const adding = ref(false);
const candidates = ref([]);
const toAdd = ref([]);
const dragFrom = ref(null);
const dragOver = ref(null);
const splitError = ref('');
const preview = ref(null);
const itemDraft = ref(null);

async function load() {
  set.value = await getMediaSet(props.setId);
  name.value = set.value.name;
  setDate.value = set.value.set_date;
  progressionId.value = set.value.progression_id;
  order.value = [...set.value.media];
  const lib = await listPatientMedia(set.value.patient_unique_id);
  progressions.value = lib.progressions;
}

function move(i, d) {
  const j = i + d;
  const next = [...order.value];
  [next[i], next[j]] = [next[j], next[i]];
  order.value = next;
}
function dropAt(i) {
  if (dragFrom.value === null || dragFrom.value === i) return;
  const next = [...order.value];
  const [it] = next.splice(dragFrom.value, 1);
  next.splice(i, 0, it);
  order.value = next;
  dragFrom.value = dragOver.value = null;
}
function takeOut(it) {
  order.value = order.value.filter((x) => x.id !== it.id);
  takenOut.value.push(it.id);
}

async function openAdd() {
  const lib = await listPatientMedia(set.value.patient_unique_id);
  const inSet = new Set(order.value.map((x) => x.id));
  candidates.value = lib.items.filter((it) => !inSet.has(it.id));
  toAdd.value = [];
  adding.value = true;
}
function toggleAdd(id) {
  const i = toAdd.value.indexOf(id);
  if (i >= 0) toAdd.value.splice(i, 1); else toAdd.value.push(id);
}

async function newProgression() {
  const res = await Swal.fire({ title: 'New progression', input: 'text', inputValue: name.value.replace(/ — .*$/, '') + ' progression', showCancelButton: true, confirmButtonText: 'Create', inputValidator: (v) => (!String(v || '').trim() ? 'Give it a name.' : undefined) });
  if (!res.isConfirmed) return;
  const p = await store.run((api) => api.saveProgression({ patient_unique_id: set.value.patient_unique_id, name: res.value, primary_tag: set.value.cover && set.value.cover.primary_tag, body_site: set.value.cover && set.value.cover.body_site }), { success: 'Progression created.' });
  if (p) { progressions.value.push(p); progressionId.value = p.id; }
}

async function renameProgression() {
  const p = progressions.value.find((x) => x.id === progressionId.value);
  if (!p) return;
  const res = await Swal.fire({ title: 'Rename progression', input: 'text', inputValue: p.name, showCancelButton: true, confirmButtonText: 'Rename', inputValidator: (v) => (!String(v || '').trim() ? 'Give it a name.' : undefined) });
  if (!res.isConfirmed) return;
  const out = await store.run((api) => api.saveProgression({ id: p.id, name: res.value }), { success: 'Progression renamed.', bump: true });
  if (out) p.name = out.name;
}

// An item's own details, edited without leaving the set (the same form as the viewer's Edit details).
function startItemEdit(it) {
  itemDraft.value = {
    id: it.id,
    fields: {
      patient_unique_id: it.patient_unique_id, note: it.note || '', primary_tag: it.primary_tag, tags: [...(it.tags || [])],
      body_site: it.body_site, body_site_detail: it.body_site_detail || '', orddiagid: it.orddiagid, patient_visible: it.patient_visible,
      captured_by_unique_id: it.captured_by_unique_id,
    },
  };
}
async function saveItemEdit() {
  const { id, fields } = itemDraft.value;
  const out = await store.run((api) => api.updateMediaItem(id, fields), { success: 'Details saved.' });
  if (!out) return;
  order.value = order.value.map((x) => (x.id === id ? out : x));
  itemDraft.value = null;
}

async function save() {
  const added = toAdd.value.filter((id) => !order.value.some((x) => x.id === id));
  const saved = await store.run((api) => api.saveMediaSet({
    id: set.value.id, name: name.value, set_date: setDate.value, progression_id: progressionId.value,
    add_item_ids: added, remove_item_ids: takenOut.value,
  }));
  if (!saved) return;
  const ids = [...order.value.map((x) => x.id), ...added];
  const out = await store.run((api) => api.reorderSet(set.value.id, ids), { success: 'Set saved.', bump: true });
  if (out) emit('saved', out);
}

async function reassign() {
  splitError.value = '';
  const others = store.patients.filter((p) => p.unique_ID !== set.value.patient_unique_id);
  const res = await Swal.fire({
    title: 'Assign this set to a different patient',
    input: 'select',
    inputOptions: Object.fromEntries(others.map((p) => [p.unique_ID, `${p.name} (DOB ${p.date_of_birth})`])),
    inputPlaceholder: 'Choose the patient',
    showCancelButton: true,
    confirmButtonText: 'Continue',
    inputValidator: (v) => (!v ? 'Choose a patient.' : undefined),
  });
  if (!res.isConfirmed) return;
  const sure = await Swal.fire({
    title: 'Are you sure?',
    html: `Are you sure you wish to reassign the media set “${escapeHtml(set.value.name)}” (${order.value.length} items) from <b>${escapeHtml(set.value.patient_name)}</b> to <b>${escapeHtml(patientName(res.value))}</b>?${set.value.progression_id ? ' It leaves its progression.' : ''} This is logged.`,
    icon: 'warning', showCancelButton: true, confirmButtonText: 'Yes, reassign', cancelButtonText: 'No',
  });
  if (!sure.isConfirmed) return;
  try {
    const out = await reassignMedia({ type: 'set', id: set.value.id }, res.value);
    store.say(`Set moved to ${out.to_patient_name} (${out.moved} items).`);
    store.setPatient(res.value);
    store.bump();
    emit('moved', res.value);
  } catch (e) {
    if (e.code === 'would_split') splitError.value = `Can’t reassign: ${e.message}`;
    else store.say(e.message, 'error');
  }
}

async function removeSet() {
  const res = await Swal.fire({
    title: 'Delete this set?', html: 'The set goes; its items stay in the patient’s library and in any note that references them.',
    input: 'text', inputPlaceholder: 'Reason (optional for your own sets)', showCancelButton: true, confirmButtonText: 'Delete set', confirmButtonColor: '#c0392b',
  });
  if (!res.isConfirmed) return;
  const out = await store.run((api) => api.removeMedia({ type: 'set', id: set.value.id }, res.value || ''), { success: 'Set deleted; its items are still in the library.', bump: true });
  if (out) emit('deleted');
}

// Set names are typed by people; Swal's `html` renders markup.
const escapeHtml = (t) => String(t ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const fmt = (iso) => new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
onMounted(load);
watch(() => props.setId, () => { takenOut.value = []; adding.value = false; splitError.value = ''; preview.value = null; itemDraft.value = null; load(); });
</script>

<style scoped>
.md-seteditor { max-width:900px; background:#fff; }
.md-seteditor-head { display:flex; align-items:center; gap:12px; flex-wrap:wrap; padding-bottom:12px; }
.md-seteditor-head .md-back { flex-basis:100%; }
.md-seteditor-head h2 { font-size:22px; }
.md-seteditor-body { padding:4px 0 8px; }
.md-seteditor-two { display:grid; grid-template-columns:2fr 1fr; gap:0 12px; }
.md-seteditor-items { display:grid; grid-template-columns:repeat(auto-fill, minmax(150px, 1fr)); gap:12px; }
.md-seteditor-item { position:relative; border:2px solid var(--line); border-radius:10px; overflow:hidden; background:#fff; cursor:grab; }
.md-seteditor-item.dragging { opacity:.4; }
.md-seteditor-item.over { border-color:var(--teal); }
.md-seteditor-pos { position:absolute; top:6px; left:6px; z-index:1; background:var(--teal); color:#fff; border-radius:6px; padding:0 7px; font-size:13px; font-weight:700; }
.md-seteditor-meta { padding:6px 8px 0; }
.md-seteditor-note { padding:0 8px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.md-seteditor-open { cursor:zoom-in; }
.md-seteditor-edit { position:absolute; top:6px; right:6px; z-index:1; width:28px; height:28px; border:none; border-radius:6px; background:rgba(255,255,255,.9); color:var(--ink); font-size:15px; cursor:pointer; }
.md-seteditor-edit:hover { background:#eef4f4; }
.md-seteditor-preview { position:relative; background:#0d1417; border-radius:10px; overflow:hidden; }
.md-seteditor-preview-stage { height:min(50vh, 420px); }
.md-seteditor-preview p { margin:0; padding:8px 12px; color:#cfd8da; }
.md-seteditor-preview-back { position:absolute; top:10px; right:10px; z-index:1; }
.md-seteditor-itemedit { padding:12px; margin-top:14px; }
.md-seteditor-actions { padding:4px 8px 8px; gap:6px; }
.md-seteditor-actions .md-iconbtn { width:30px; height:30px; font-size:16px; }
.md-seteditor-add { padding:12px; margin-top:14px; }
.md-seteditor-split { margin-top:14px; }
.md-seteditor-foot { display:flex; gap:10px; flex-wrap:wrap; padding:12px 0; border-top:1px solid var(--line); position:sticky; bottom:0; background:#fff; }
/* Viewer first: a white panel over the picture. */
.md-seteditor.is-immersive { position:absolute; top:0; right:0; bottom:0; width:min(560px, 100%); overflow:auto; padding:16px 18px 0; box-shadow:-10px 0 30px rgba(0,0,0,.35); z-index:3; color:var(--ink); }
.md-seteditor.is-immersive .md-seteditor-head .md-back { flex-basis:auto; }
.md-seteditor.is-panel .md-seteditor-two { grid-template-columns:1fr; }
@media (max-width: 640px) { .md-seteditor-two { grid-template-columns:1fr; } }
</style>
